import { inject, Injectable } from '@angular/core';
import { AuthFirebaseService } from '../api/auth-firebase/auth-firebase.service';
import { UserService } from '../user/user.service';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import {
  catchError,
  finalize,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { isDoodAnonymousJwt, isTokenExpired } from '@core/helpers/dood-anonymous-jwt.helper';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { DoodUserModel } from '@store/authentication/authentication.model';

@Injectable({
  providedIn: 'root',
})
export class AnonymousAuthenticationService {
  private readonly authFirebaseService = inject(AuthFirebaseService);
  private readonly authStoreSelector = inject(AuthStoreSelector);
  private readonly userService = inject(UserService);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly analyticsService = inject(AnalyticsService);

  // Every POST /users/anonymous mints a new guest. Concurrent callers (kiosk layout +
  // preloader block at boot) must share one creation, or the store ends up with the JWT of
  // one guest and the user of another, and the API rejects their orders (DOOD-3495).
  private guestSessionInFlight$: Observable<DoodUserModel | undefined> | null = null;
  private ensureSessionInFlight$: Observable<unknown> | null = null;

  /**
   * Create a Dood guest session (POST /users/anonymous) and load /users/me.
   * No Firebase signInAnonymously. Concurrent calls share the same creation.
   */
  createAnonymousSession$(): Observable<DoodUserModel | undefined> {
    this.guestSessionInFlight$ ??= this.createGuestUnlessRealSession$().pipe(
      finalize(() => (this.guestSessionInFlight$ = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.guestSessionInFlight$;
  }

  /**
   * Restore a stored Dood guest JWT, or mint one if missing/expired.
   * Call this explicitly from kiosk / anonymous-preloader — never from DI.
   * Concurrent calls share the same bootstrap.
   */
  ensureAnonymousSession$(): Observable<unknown> {
    // Preview uses scenario stubs + a disconnected auth snapshot — no anonymous
    // API bootstrap (POST /users/anonymous + GET /users/me).
    if (this.cb2PreviewState.isActive()) {
      return of(null);
    }

    this.ensureSessionInFlight$ ??= this.restoreOrCreateGuest$().pipe(
      finalize(() => (this.ensureSessionInFlight$ = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.ensureSessionInFlight$;
  }

  /**
   * A guest on top of a real Firebase session gets the Firebase token re-injected by
   * isAuthenticate$() while the store keeps the guest user. Restore the real account instead.
   */
  private createGuestUnlessRealSession$(): Observable<DoodUserModel | undefined> {
    return this.authFirebaseService.isAuthenticate$().pipe(
      take(1),
      switchMap(firebaseUser => {
        if (firebaseUser && !firebaseUser.isAnonymous) {
          this.analyticsService.log({
            level: 'warn',
            label: 'guest_creation_skipped_real_session',
            message: { storeUserId: this.authStoreSelector.selectUser()?.id ?? null },
          });
          return this.authFirebaseService.loadUser$();
        }

        console.log('[AnonymousAuthentication] Creating Dood anonymous session');
        return this.userService.createAnonymousUser$().pipe(
          switchMap(() => this.userService.loadUser$()),
          tap(() => this.authFirebaseService.startAnonymousUserLogoutTimer()),
          tap(() => console.log('[AnonymousAuthentication] Authentication succeeded')),
        );
      }),
    );
  }

  private restoreOrCreateGuest$(): Observable<unknown> {
    return this.authFirebaseService.isAuthenticate$().pipe(
      withLatestFrom(
        this.authStoreSelector.selectUserIsLoaded$,
        this.authStoreSelector.selectUserIsLoading,
        this.authStoreSelector.selectStatus$,
      ),
      take(1),
      switchMap(([firebaseUser, isUserLoaded, isUserLoading, status]) => {
        // Real Firebase account: AuthFirebaseService will load /users/me — do not mint a guest.
        if (firebaseUser && !firebaseUser.isAnonymous) {
          console.log('[AnonymousAuthentication] Real Firebase session present');
          return of(null);
        }

        if (isUserLoading) {
          console.log('[AnonymousAuthentication] User load already in progress');
          return of(null);
        }

        const hasDoodToken =
          isDoodAnonymousJwt(status.token) && !isTokenExpired(status.tokenExpiresAt);

        if (hasDoodToken) {
          if (isUserLoaded || this.authStoreSelector.selectUser()) {
            console.log('[AnonymousAuthentication] Dood anonymous session already loaded');
            this.authFirebaseService.startAnonymousUserLogoutTimer();
            return of(null);
          }

          // Token hydrated but user missing — restore via /users/me, else mint a new guest.
          console.log('[AnonymousAuthentication] Restoring Dood anonymous user from token');
          return this.userService.loadUser$().pipe(
            tap(() => this.authFirebaseService.startAnonymousUserLogoutTimer()),
            catchError(() => this.createAnonymousSession$()),
          );
        }

        // Expired / missing guest — mint a new Dood JWT.
        return this.createAnonymousSession$();
      }),
    );
  }
}
